import AlertIcon from 'src/assets/Icons/Alert.svg'
import React, { FunctionComponent, useEffect } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import camelCase from 'lodash/camelCase' // this will be used for camelCasing ticketButtonText
import { shouldUseBetaCheckout } from 'src/components/calendar/calendarUtils'
import { trackGetTickets } from 'src/lib/analytics'
import { EVENT_STATES } from 'src/constants/constants'
import locale from '../locale'

const style = require('./TicketButton.css')

interface Props {
  eventId: string,
  hostId: string,
  baseUrl: string,
  listingStatus: string,
  ticketButtonText: string,
  isTimedEntry: boolean,
  viewer: any,
}

declare var $u

const TicketButtonComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventId,
  hostId,
  baseUrl,
  listingStatus,
  ticketButtonText,
  isTimedEntry,
  viewer,
  intl,
}) => {
  // BETA FEATURE: New Checkout. TODO: Pass real data, rather than URL param.
  const searchString = window.location.search
  const useBetaCheckout = shouldUseBetaCheckout(searchString)

  const getJunoUrl = () => {
    $u.open(eventId, { betaCheckoutEnabled: useBetaCheckout, lang: intl.locale })
    const trackingParams = { isTimedEntry, eventId, hostId, widget: 'button', viewer }
    trackGetTickets(trackingParams)
  }

  const loadTicketWidget = () => {
    const loadScript = document.createElement('script')
    loadScript.src = `${baseUrl}/embed2.js`
    loadScript.id = 'juno-script'
    window.document.querySelector(`.${style.container}`).appendChild(loadScript)
  }

  useEffect(() => loadTicketWidget(), [])

  const assignButtonText = (locale: ReactIntl.FormattedMessage.MessageDescriptor) => intl.formatMessage(locale).toLocaleUpperCase()

  const determineButtonText = () => {
    if (ticketButtonText !== 'get_tickets') return camelCase(ticketButtonText)
    return camelCase(ticketButtonText)
  }

  const clickable = () => {
    switch (listingStatus) {
      case EVENT_STATES.soldOutWaitlistEnabled:
      case EVENT_STATES.fanReminderWaitlistEnabled:
      case EVENT_STATES.fanReminderWaitlistEnabledWithLockedTickets:
      case EVENT_STATES.salesEnded:
      case EVENT_STATES.salesEndedEventOver:
      case EVENT_STATES.unavailableAllHidden:
      case EVENT_STATES.unavailableOnlyAtDoor:
      case EVENT_STATES.unavailableGeofenced:
      case EVENT_STATES.semiAvailableNotStarted:
      case EVENT_STATES.availableStandard:
      case EVENT_STATES.semiAvailableAllLocked:
        return true
      case EVENT_STATES.soldOutWaitlistNotEnabled:
      case EVENT_STATES.noData:
        return false
      default:
        return false
    }
  }

  const buttonClass = () => {
    switch (listingStatus) {
      case EVENT_STATES.semiAvailableNotStarted:
      case EVENT_STATES.semiAvailableAllLocked:
      case EVENT_STATES.availableStandard:
      case EVENT_STATES.fanReminderWaitlistEnabled:
      case EVENT_STATES.fanReminderWaitlistEnabledWithLockedTickets:
        return style.primary
      case EVENT_STATES.soldOutWaitlistEnabled:
        return style.secondary
      case EVENT_STATES.salesEnded:
      case EVENT_STATES.salesEndedEventOver:
      case EVENT_STATES.unavailableAllHidden:
      case EVENT_STATES.unavailableOnlyAtDoor:
      case EVENT_STATES.unavailableGeofenced:
        return style.tertiary
      case EVENT_STATES.soldOutWaitlistNotEnabled:
      case EVENT_STATES.noData:
        return style.disabled
      default:
        return style.disabled
    }
  }

  const buttonTextLocaleKey = () => {
    switch (listingStatus) {
      case EVENT_STATES.soldOutWaitlistEnabled:
        return 'joinWaitlist'
      case EVENT_STATES.fanReminderWaitlistEnabled:
        return 'remindMe'
      case EVENT_STATES.soldOutWaitlistNotEnabled:
      case EVENT_STATES.salesEnded:
      case EVENT_STATES.salesEndedEventOver:
      case EVENT_STATES.unavailableAllHidden:
      case EVENT_STATES.unavailableOnlyAtDoor:
      case EVENT_STATES.unavailableGeofenced:
      case EVENT_STATES.noData:
        return 'details'
      case EVENT_STATES.availableStandard:
      case EVENT_STATES.semiAvailableNotStarted:
      case EVENT_STATES.semiAvailableAllLocked:
      case EVENT_STATES.fanReminderWaitlistEnabledWithLockedTickets:
        return determineButtonText()
      default:
        return 'details'
    }
  }

  const getButtonIcon = (status: string): React.ReactNode => {
    switch (status) {
      case EVENT_STATES.fanReminderWaitlistEnabled:
        return <AlertIcon className={style.alertIcon} />
      default:
        return null
    }
  }

  return (
    <button className={`${style.container} ${buttonClass()}`} onClick={getJunoUrl} disabled={!clickable()}>
      {getButtonIcon(listingStatus)}
      {assignButtonText(locale[buttonTextLocaleKey()])}
    </button>
  )
}

export const TicketButton = injectIntl(TicketButtonComponent)
